import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import {
  LuHouse,
  LuRefreshCw,
  LuTriangleAlert,
  LuChevronDown,
  LuShieldAlert,
} from 'react-icons/lu'
import { useState } from 'react'

import { Button } from '#/components/ui/button'

type ErrorPageProps = {
  error?: Error | any
  reset?: () => void
  info?: string
}

// ─── Islamic Geometric Pattern Watermark ─────────────────────────────────────
function IslamicTilePattern() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.035]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="error-pattern"
          x="0"
          y="0"
          width="90"
          height="90"
          patternUnits="userSpaceOnUse"
        >
          <g fill="none" stroke="#ef4444" strokeWidth="0.6">
            <polygon points="45,4 49,33 72,20 57,40 85,45 57,50 72,70 49,57 45,86 41,57 18,70 33,50 5,45 33,40 18,20 41,33" />
            <circle cx="45" cy="45" r="16" stroke="#ef4444" strokeWidth="0.4" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#error-pattern)" />
    </svg>
  )
}

export default function ErrorPage({ error, reset, info }: ErrorPageProps) {
  const navigate = useNavigate()
  const [showDetails, setShowDetails] = useState(false)

  const errorMessage =
    error?.message ||
    (typeof error === 'string'
      ? error
      : 'An unexpected application error occurred.')

  const handleRetry = () => {
    if (reset) {
      reset()
    } else {
      window.location.reload()
    }
  }

  return (
    <div className="relative min-h-dvh flex items-center justify-center overflow-hidden bg-[#180a0a] px-6 py-24 text-white">
      <IslamicTilePattern />

      {/* Ambient Red / Amber Glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-red-600/15 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-10 h-[400px] w-[400px] rounded-full bg-amber-600/15 blur-[100px]"
      />

      <div className="container relative z-10 mx-auto max-w-xl text-center">
        {/* Glowing Shield Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-red-500/15 border border-red-500/30 text-red-400 shadow-xl shadow-red-500/20"
        >
          <LuShieldAlert className="h-10 w-10 animate-pulse" />
        </motion.div>

        {/* Heading & Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-3"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 border border-red-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-300">
            <LuTriangleAlert className="h-3.5 w-3.5" /> System Exception
          </span>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            Something Went Wrong
          </h1>
          <p className="text-sm sm:text-base text-neutral-300/80 leading-relaxed max-w-md mx-auto">
            {info ||
              'We encountered an unexpected issue while loading this resource. Please try reloading or returning home.'}
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3.5"
        >
          <Button
            onClick={handleRetry}
            className="h-12 cursor-pointer gap-2 rounded-xl bg-primary-500 px-6 font-semibold text-neutral-950 hover:bg-primary-400 shadow-lg shadow-primary-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <LuRefreshCw className="h-4 w-4" />
            Try Again
          </Button>

          <Button
            variant="outline"
            onClick={() => navigate({ to: '/' })}
            className="h-12 cursor-pointer gap-2 rounded-xl border-white/20 bg-white/5 px-6 font-semibold text-white hover:border-primary-400 hover:bg-white/10 hover:text-primary-300 transition-all"
          >
            <LuHouse className="h-4 w-4" />
            Back to Home
          </Button>
        </motion.div>

        {/* Optional Error Diagnostics Collapsible */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 text-left"
          >
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="mx-auto flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-400 hover:text-neutral-200 transition-colors"
            >
              <span>{showDetails ? 'Hide' : 'Show'} Technical Details</span>
              <LuChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  showDetails ? 'rotate-180' : ''
                }`}
              />
            </button>

            {showDetails && (
              <div className="mt-3 rounded-xl border border-red-500/20 bg-red-950/40 p-4 font-mono text-xs text-red-200 backdrop-blur-md overflow-x-auto">
                <p className="font-bold text-red-300 mb-1">Error Trace:</p>
                <p className="break-all">{errorMessage}</p>
                {error?.stack && (
                  <pre className="mt-2 text-[11px] text-neutral-400 overflow-x-auto whitespace-pre-wrap max-h-40">
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}
